"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.displayErrors = exports.displayIssues = exports.displayDependencies = exports.selectDisplayStrategy = exports.displaySignatures = void 0;
const chalk = require("chalk");
const common_1 = require("./common");
const object_1 = require("../utils/object");
function displaySignatures(scanResults) {
    const result = [chalk.whiteBright('Signatures')];
    for (const { facts = [] } of scanResults) {
        for (const { data = [] } of facts) {
            for (const { path, hashes_ffm } of data) {
                if (path && (hashes_ffm === null || hashes_ffm === void 0 ? void 0 : hashes_ffm.length) && hashes_ffm[0].data) {
                    result.push(`${hashes_ffm[0].data} ${path}`);
                }
            }
        }
    }
    if (result.length) {
        result.push('');
    }
    return result;
}
exports.displaySignatures = displaySignatures;
function findDependencyLines(depGraph, options, depsFilePaths, fileSignaturesDetails) {
    const displayDepsWithPaths = (options && options['print-dep-paths']) || false;
    const displayDeps = (options && options['print-deps']) || false;
    if (displayDepsWithPaths) {
        return displayDependencies(depGraph, fileSignaturesDetails, depsFilePaths);
    }
    else if (displayDeps) {
        return displayDependencies(depGraph, fileSignaturesDetails);
    }
    return [];
}
function selectDisplayStrategy(options, depGraph, testResult) {
    const { depsFilePaths, issues, issuesData, fileSignaturesDetails, } = testResult;
    const dependencySection = findDependencyLines(depGraph, options, depsFilePaths, fileSignaturesDetails);
    const issuesSection = displayIssues(depGraph, issues, issuesData);
    return [dependencySection, issuesSection];
}
exports.selectDisplayStrategy = selectDisplayStrategy;
function computeDependencyName(name, version) {
    return `${name}@${version || 'unknown'}`;
}
function computeDependencyId(name, version) {
    return `${name}@${version || ''}`;
}
function displayDependencies(depGraph, fileSignaturesDetails, depsFilePaths = {}) {
    var _a, _b;
    let result = [];
    const dependencies = depGraph === null || depGraph === void 0 ? void 0 : depGraph.getDepPkgs();
    const hasDependencies = (dependencies === null || dependencies === void 0 ? void 0 : dependencies.length) > 0;
    if (!hasDependencies) {
        return result;
    }
    result.push(chalk.whiteBright('\nDependencies:\n'));
    for (const { name, version } of dependencies) {
        const dependencyId = computeDependencyId(name, version);
        const dependencyName = computeDependencyName(name, version);
        result.push(`\n${common_1.leftPad(dependencyName, 2)}`);
        if (fileSignaturesDetails && ((_a = fileSignaturesDetails[dependencyId]) === null || _a === void 0 ? void 0 : _a.purl)) {
            result.push(common_1.leftPad(`purl: ${fileSignaturesDetails[dependencyId].purl}`, 2));
        }
        if (fileSignaturesDetails && ((_b = fileSignaturesDetails[dependencyId]) === null || _b === void 0 ? void 0 : _b.confidence)) {
            result.push(common_1.leftPad(`confidence: ${fileSignaturesDetails[dependencyId].confidence.toFixed(3)}`, 2));
        }
        if (!object_1.isEmpty(depsFilePaths)) {
            const displayDepsFilePathsOutput = displayDepsFilePaths(depsFilePaths, dependencyId);
            result = [...result, ...displayDepsFilePathsOutput];
        }
    }
    if (result.length) {
        result.push('');
    }
    return result;
}
exports.displayDependencies = displayDependencies;
function displayDepsFilePaths(depsFilePaths, dependencyId) {
    const maxFilePathsToBeDisplayed = 3;
    const result = [];
    result.push(`${common_1.leftPad('matching files:', 2)}`);
    const filePathsToDisplay = depsFilePaths[dependencyId].slice(0, maxFilePathsToBeDisplayed);
    for (const filePathToDisplay of filePathsToDisplay) {
        result.push(common_1.leftPad(`- ${filePathToDisplay}`, 4));
    }
    if (depsFilePaths[dependencyId].length > 3) {
        result.push(common_1.leftPad(`... and ${depsFilePaths[dependencyId].length -
            maxFilePathsToBeDisplayed} more files`, 4));
    }
    return result;
}
function displayIssues(depGraph, issues, issuesData) {
    const result = [];
    const dependencies = depGraph === null || depGraph === void 0 ? void 0 : depGraph.getDepPkgs();
    const dependenciesCountMsg = (dependencies === null || dependencies === void 0 ? void 0 : dependencies.length) == 1
        ? '1 dependency'
        : `${dependencies === null || dependencies === void 0 ? void 0 : dependencies.length} dependencies`;
    const issuesCount = issues.length == 1 ? '1 issue' : `${issues.length} issues`;
    const hasIssues = issues.length > 0;
    if (hasIssues) {
        const nonLicenseIssues = issues.filter((issue) => {
            var _a;
            return ((_a = issuesData[issue.issueId]) === null || _a === void 0 ? void 0 : _a.type) !== 'license';
        });
        if (nonLicenseIssues.length > 0) {
            prepareResults(result, nonLicenseIssues, issuesData, 'Issues:');
        }
        const licenseIssues = issues.filter((issue) => { var _a; return ((_a = issuesData[issue.issueId]) === null || _a === void 0 ? void 0 : _a.type) === 'license'; });
        if (licenseIssues.length > 0) {
            prepareResults(result, licenseIssues, issuesData, 'License issues:');
        }
    }
    const issuesFound = hasIssues
        ? chalk.redBright(issuesCount)
        : chalk.greenBright(issuesCount);
    const identifiedUnmanagedDeps = `Tested ${dependenciesCountMsg} for known issues, found ${issuesFound}.\n`;
    if ((dependencies === null || dependencies === void 0 ? void 0 : dependencies.length) > 0) {
        result.push(identifiedUnmanagedDeps);
    }
    return result;
}
exports.displayIssues = displayIssues;
function prepareResults(result, issues, issuesData, typeTitle) {
    result.push(chalk.bold(typeTitle));
    for (const { pkgName: name, pkgVersion: version, issueId: vulnId, } of issues) {
        const { title, severity, legalInstructionsArray } = issuesData[vulnId];
        const color = common_1.getColorBySeverity(severity);
        const severityAndTitle = color.bold(`\n ✗ [${common_1.capitalize(severity)}] ${title}`);
        const dependencyName = computeDependencyName(name, version);
        const vulnDetailsUrl = `https://security.snyk.io/vuln/${vulnId}`;
        const introducedThrough = common_1.leftPad(`Introduced through: ${dependencyName}`, 3);
        const urlText = common_1.leftPad(`URL: ${vulnDetailsUrl}`, 3);
        result.push(severityAndTitle);
        result.push(introducedThrough);
        result.push(urlText);
        if (legalInstructionsArray) {
            result.push(common_1.leftPad(chalk.bold(`Legal instructions:`), 3));
            const { licenseName, legalContent } = legalInstructionsArray[0];
            const legalInstructionsText = common_1.leftPad(`○ for ${licenseName}: ${legalContent}`, 3);
            result.push(legalInstructionsText);
        }
    }
    result.push('');
}
function displayErrors(errors) {
    const result = [];
    if (errors.length) {
        result.push(chalk.redBright('Errors'));
    }
    for (const error of errors) {
        result.push(error);
    }
    if (result.length) {
        result.push('');
    }
    return result;
}
exports.displayErrors = displayErrors;
//# sourceMappingURL=display.js.map